import { Component, OnInit, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { take, forkJoin } from 'rxjs';

import { Person } from '../../objects/person';
import { Contract } from '../../objects/contract';
import { PeopleService } from '../../service/entity-services/people.service';
import { ContractService } from '../../service/entity-services/contract.service';

export interface PeopleTableRow {
  person: Person;
  source: string;
}

@Component({
  selector: 'app-people-table',
  templateUrl: './people-table.component.html',
  styleUrls: ['./people-table.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule
  ]
})
export class PeopleTableComponent implements OnInit {
  private peopleService = inject(PeopleService);
  private contractService = inject(ContractService);
  private router = inject(Router);

  @Input() contractIds: string[] = [];

  rows = signal<PeopleTableRow[]>([]);
  loading = signal<boolean>(true);
  displayedColumns = ['name', 'title', 'email', 'phone', 'company', 'source'];

  ngOnInit(): void {
    this.loadPeople();
  }

  private loadPeople(): void {
    if (!this.contractIds || this.contractIds.length === 0) {
      this.loading.set(false);
      return;
    }

    this.contractService.allContracts$.pipe(take(1)).subscribe(allContracts => {
      const contactsBySource = new Map<string, string>();

      for (const id of this.contractIds) {
        const contract = allContracts.find(c => c.contractId === id || c.contractNum === id);
        if (contract?.contacts && Array.isArray(contract.contacts)) {
          const contractName = contract.nameOfContract || contract.contractNum || id;
          for (const contactNum of contract.contacts) {
            if (contactNum && !contactsBySource.has(contactNum)) {
              contactsBySource.set(contactNum, contractName);
            }
          }
        }
      }

      if (contactsBySource.size === 0) {
        this.loading.set(false);
        return;
      }

      const contactNums = Array.from(contactsBySource.keys());
      const hydrationObservables = contactNums.map(num =>
        this.peopleService.getPersonFullyHydrated(num)
      );

      forkJoin(hydrationObservables).subscribe({
        next: (people) => {
          this.rows.set(
            people
              .filter((p): p is Person => p !== null)
              .map(person => ({
                person,
                source: contactsBySource.get(person.contactNum!) || 'Contract'
              }))
          );
          this.loading.set(false);
        },
        error: (err) => {
          console.error('Error loading people:', err);
          this.loading.set(false);
        }
      });
    });
  }

  navigateToPerson(person: Person): void {
    if (person.contactNum) {
      this.router.navigate(['/securehome/person', person.contactNum]);
    }
  }
}
